/**
 * A labelled fact on a record's detail card (docs/03 §4c).
 *
 * The most repeated primitive in the app, and — until now — the only one with
 * no home in the design system. It had been re-declared in ELEVEN feature files
 * as a byte-identical local `Fact`, plus twice inline in `GroupScreens`, which
 * is thirteen places for the fourteenth to differ.
 *
 * ===========================================================================
 * Why the label is sentence case, not the ALL CAPS it used to be
 * ===========================================================================
 *
 * A field must be labelled the SAME WAY whether you are reading it or typing
 * into it. Those thirteen copies rendered the label at `text-chip` (12px), all
 * caps, with letter-spacing. The edit form for the very same field renders it at
 * `text-meta` (13px), sentence case, via `FieldShell`. So a property manager
 * looking at "Service category" on the detail card and then opening the form to
 * change it was reading two differently-shaped labels for one field — and in
 * compact density, where both collapse to 12px, the ONLY remaining difference
 * was the casing, which is the half that hurts.
 *
 * That is the whole argument. It is not a taste preference about capitals: it
 * is that a data-entry app makes people cross between reading a record and
 * editing it constantly, and the label is the thing they navigate by.
 *
 * The counter-argument, taken seriously: all-caps micro-labels separate label
 * from value in a dense grid without needing a colon. True — but the separation
 * here is already carried twice over, by SIZE (13px vs 15px) and by COLOR
 * (`text-ink2` vs `text-ink`). Casing was a third, redundant signal bought at
 * the cost of legibility, since all-caps removes the word-shape cues people
 * actually read short labels by.
 *
 * `text-transform` is presentational, so nothing about this changes the
 * accessible name or any test's `getByText` — the DOM text was always sentence
 * case. This makes what is rendered agree with what was already written.
 */

import type { ReactNode } from 'react';

export function Fact({
  label,
  value,
}: {
  readonly label: string;
  /**
   * An em dash when there is nothing. A detail card states what IS known, so a
   * missing value must read as "we do not have this" rather than as a gap in
   * the layout that looks like a rendering bug.
   */
  readonly value: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-meta font-medium text-ink2">{label}</span>
      <span className="text-body text-ink">{value ?? '—'}</span>
    </div>
  );
}
